import { useState } from "react";
import {
  View,
  Text,
  ScrollView,
  TextInput,
  Pressable,
  Alert,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Modal,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { useThemeColors } from "@/providers/ThemeProvider";
import { logWeekendAction } from "@/lib/activity-log";
import DateTimePicker from "@react-native-community/datetimepicker";
import { format } from "date-fns";
import { nl } from "date-fns/locale";

/**
 * Input field component
 */
interface InputFieldProps {
  label: string;
  value: string;
  onChangeText: (text: string) => void;
  placeholder: string;
  keyboardType?: "default" | "numeric";
  multiline?: boolean;
  colors: ReturnType<typeof useThemeColors>;
}

const InputField = ({
  label,
  value,
  onChangeText,
  placeholder,
  keyboardType = "default",
  multiline = false,
  colors,
}: InputFieldProps) => (
  <View className="mb-4">
    <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
      {label}
    </Text>
    <TextInput
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      placeholderTextColor={colors.textTertiary}
      keyboardType={keyboardType}
      multiline={multiline}
      numberOfLines={multiline ? 3 : 1}
      className={`rounded-xl px-4 py-3 ${multiline ? "h-24" : ""}`}
      style={{
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.border,
        color: colors.text,
        textAlignVertical: multiline ? "top" : "center",
      }}
    />
  </View>
);

/**
 * Date picker field component
 */
interface DateFieldProps {
  label: string;
  value: Date | null;
  onDateChange: (date: Date | null) => void;
  placeholder: string;
  colors: ReturnType<typeof useThemeColors>;
}

const DateField = ({ label, value, onDateChange, placeholder, colors }: DateFieldProps) => {
  const [showPicker, setShowPicker] = useState(false);
  const [tempDate, setTempDate] = useState<Date>(value || new Date());

  const handleChange = (event: any, selectedDate?: Date) => {
    if (Platform.OS === "android") {
      setShowPicker(false);
      if (event.type === "set" && selectedDate) {
        onDateChange(selectedDate);
      }
    } else {
      if (selectedDate) {
        setTempDate(selectedDate);
      }
    }
  };

  const handleConfirmIOS = () => {
    onDateChange(tempDate);
    setShowPicker(false);
  };

  const handleCancelIOS = () => {
    setShowPicker(false);
  };

  const handleClear = () => {
    onDateChange(null);
  };

  const formattedDate = value
    ? format(value, "d MMMM yyyy", { locale: nl })
    : null;

  return (
    <View className="mb-4">
      <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
        {label}
      </Text>
      <Pressable
        onPress={() => setShowPicker(true)}
        className="rounded-xl px-4 py-3 flex-row items-center justify-between"
        style={{
          backgroundColor: colors.surface,
          borderWidth: 1,
          borderColor: colors.border,
        }}
      >
        <Text style={{ color: formattedDate ? colors.text : colors.textTertiary }}>
          {formattedDate || placeholder}
        </Text>
        <View className="flex-row items-center">
          {value && (
            <Pressable onPress={handleClear} className="mr-2 p-1">
              <Ionicons name="close-circle" size={20} color={colors.textTertiary} />
            </Pressable>
          )}
          <Ionicons name="calendar-outline" size={20} color="#7C3AED" />
        </View>
      </Pressable>

      {/* iOS Modal Picker */}
      {Platform.OS === "ios" && showPicker && (
        <Modal transparent animationType="slide">
          <View className="flex-1 justify-end bg-black/30">
            <View className="bg-white rounded-t-3xl">
              <View className="flex-row justify-between items-center px-4 py-3 border-b border-gray-200">
                <Pressable onPress={handleCancelIOS}>
                  <Text className="text-gray-500 font-medium">Annuleer</Text>
                </Pressable>
                <Text className="text-gray-800 font-semibold">{label}</Text>
                <Pressable onPress={handleConfirmIOS}>
                  <Text className="text-primary-600 font-semibold">Klaar</Text>
                </Pressable>
              </View>
              <DateTimePicker
                value={tempDate}
                mode="date"
                display="spinner"
                onChange={handleChange}
                locale="nl-NL"
                style={{ height: 200 }}
              />
              <View className="h-8" />
            </View>
          </View>
        </Modal>
      )}

      {/* Android Inline Picker */}
      {Platform.OS === "android" && showPicker && (
        <DateTimePicker
          value={tempDate}
          mode="date"
          display="default"
          onChange={handleChange}
        />
      )}
    </View>
  );
};

/**
 * Create new weekend screen
 */
export default function CreateWeekendScreen() {
  const router = useRouter();
  const { user } = useAuth();
  const colors = useThemeColors();

  const [isLoading, setIsLoading] = useState(false);

  // Form state
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [name, setName] = useState("");
  const [theme, setTheme] = useState("");
  const [locationName, setLocationName] = useState("");
  const [locationAddress, setLocationAddress] = useState("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  /**
   * Validate form
   */
  const validateForm = (): boolean => {
    if (!year.trim() || isNaN(Number(year))) {
      Alert.alert("Fout", "Voer een geldig jaar in");
      return false;
    }

    if (!name.trim()) {
      Alert.alert("Fout", "Voer een naam in voor het weekend");
      return false;
    }

    return true;
  };

  /**
   * Execute create weekend
   */
  const executeCreate = async () => {
    setIsLoading(true);

    try {
      const { data, error } = await supabase
        .from("family_weekends")
        .insert({
          year: Number(year),
          name: name.trim(),
          theme: theme.trim() || null,
          location_name: locationName.trim() || null,
          location_address: locationAddress.trim() || null,
          start_date: startDate ? format(startDate, "yyyy-MM-dd") : null,
          end_date: endDate ? format(endDate, "yyyy-MM-dd") : null,
          is_active: false,
          created_by: user?.id,
        })
        .select()
        .single();

      if (error) {
        if (error.code === "23505") {
          Alert.alert("Fout", `Er bestaat al een weekend voor ${year}`);
        } else {
          throw error;
        }
        return;
      }

      // Log weekend creation
      if (user && data) {
        logWeekendAction(user.id, data.id, name, "create", {
          year: Number(year),
          theme: theme || null,
          location_name: locationName || null,
        });
      }

      Alert.alert("Succes", `"${name}" is aangemaakt!`, [
        {
          text: "OK",
          onPress: () => router.back(),
        },
      ]);
    } catch (error) {
      console.error("Error creating weekend:", error);
      Alert.alert("Fout", "Kon weekend niet aanmaken");
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle create weekend with confirmation
   */
  const handleCreate = () => {
    if (!validateForm()) return;

    Alert.alert(
      "Weekend Aanmaken",
      `Weet je zeker dat je "${name.trim()}" (${year}) wilt aanmaken?`,
      [
        { text: "Annuleer", style: "cancel" },
        {
          text: "Aanmaken",
          onPress: executeCreate,
        },
      ]
    );
  };

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: colors.background }}
      edges={["bottom"]}
    >
      <KeyboardAvoidingView
        className="flex-1"
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        keyboardVerticalOffset={Platform.OS === "ios" ? 90 : 0}
      >
        <ScrollView
          className="flex-1 px-4"
          contentContainerStyle={{ paddingBottom: 40 }}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {/* Info Card */}
          <View
            className="rounded-xl p-4 mt-4 mb-6"
            style={{
              backgroundColor: "#F3E8FF",
              borderWidth: 1,
              borderColor: "#DDD6FE",
            }}
          >
            <View className="flex-row items-start">
              <Ionicons name="information-circle" size={20} color="#7C3AED" />
              <Text className="text-sm ml-2 flex-1" style={{ color: "#5B21B6" }}>
                Het nieuwe weekend wordt als inactief aangemaakt. Je kunt het later activeren via
                de weekenden lijst.
              </Text>
            </View>
          </View>

          {/* Form */}
          <View
            className="rounded-xl p-4"
            style={{
              backgroundColor: colors.surface,
              borderWidth: 1,
              borderColor: colors.border,
            }}
          >
            <InputField
              label="Jaar *"
              value={year}
              onChangeText={setYear}
              placeholder="2026"
              keyboardType="numeric"
              colors={colors}
            />

            <InputField
              label="Naam *"
              value={name}
              onChangeText={setName}
              placeholder="Staatsen Familie Weekend"
              colors={colors}
            />

            <InputField
              label="Thema"
              value={theme}
              onChangeText={setTheme}
              placeholder="Bijv. Beach Party, Winter Wonderland"
              colors={colors}
            />

            <InputField
              label="Locatie naam"
              value={locationName}
              onChangeText={setLocationName}
              placeholder="Bijv. Vakantiepark De Eemhof"
              colors={colors}
            />

            <InputField
              label="Locatie adres"
              value={locationAddress}
              onChangeText={setLocationAddress}
              placeholder="Adres van de locatie"
              multiline
              colors={colors}
            />

            <View className="flex-row">
              <View className="flex-1 mr-2">
                <DateField
                  label="Startdatum"
                  value={startDate}
                  onDateChange={setStartDate}
                  placeholder="Selecteer datum"
                  colors={colors}
                />
              </View>
              <View className="flex-1 ml-2">
                <DateField
                  label="Einddatum"
                  value={endDate}
                  onDateChange={setEndDate}
                  placeholder="Selecteer datum"
                  colors={colors}
                />
              </View>
            </View>
          </View>

          {/* Create Button - Purple color */}
          <Pressable
            onPress={handleCreate}
            disabled={isLoading}
            className="mt-6 rounded-xl py-4 flex-row items-center justify-center"
            style={{
              backgroundColor: isLoading ? "#A78BFA" : "#7C3AED",
            }}
            accessibilityRole="button"
            accessibilityLabel="Weekend aanmaken"
          >
            {isLoading ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <>
                <Ionicons name="checkmark-circle" size={20} color="#FFFFFF" />
                <Text className="text-white font-semibold ml-2">Weekend Aanmaken</Text>
              </>
            )}
          </Pressable>

          <View className="h-8" />
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
